import * as React from 'react';
import {
  DatePicker as AriaDatePicker,
  DatePickerProps as AriaDatePickerProps,
  DateInput,
  DateSegment,
  Dialog,
  Group,
  Label,
  Popover,
  Calendar,
  CalendarGrid,
  CalendarCell,
  Heading,
  Button as AriaButton,
  DateValue,
  OverlayArrow,
} from 'react-aria-components';
import { CalendarDate, parseDate, getLocalTimeZone, today } from '@internationalized/date';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface DatePickerProps extends Omit<AriaDatePickerProps<DateValue>, 'children'> {
  className?: string;
  placeholder?: string;
  formatDate?: (date: Date) => string;
  disabled?: boolean;
  inModal?: boolean;
  calendarButtonLabel?: string;
}

export function DatePicker({
  className,
  placeholder = 'Seleccionar fecha',
  formatDate,
  disabled,
  inModal = false,
  calendarButtonLabel = 'Abrir calendario',
  ...props
}: DatePickerProps) {
  const ariaLabel = props['aria-label'] ?? placeholder;

  return (
    <AriaDatePicker
      className={cn('group flex flex-col gap-1', className)}
      isDisabled={disabled}
      data-slot="date-picker"
      aria-label={ariaLabel}
      {...props}
    >
      <Group className="flex items-center border-0 border-b-1 border-gray-300 rounded-none bg-transparent pr-3 py-1 text-base transition-[color,box-shadow] focus-within:border-primary focus-within:border-b-2 focus-within:ring-0 focus-within:ring-offset-0 md:text-sm">
        <DateInput className="flex flex-1 items-center min-w-0">
          {(segment) => (
            <DateSegment
              segment={segment}
              className={cn(
                'inline rounded p-0.5 text-foreground tabular-nums outline-none focus:bg-accent focus:text-accent-foreground',
                segment.isPlaceholder && 'text-muted-foreground'
              )}
            />
          )}
        </DateInput>
        <AriaButton
          aria-label={calendarButtonLabel}
          className="ml-2 p-1 rounded hover:bg-accent transition-colors outline-none focus:ring-0 focus:ring-offset-0"
        >
          <CalendarIcon className="h-4 w-4 text-muted-foreground" />
        </AriaButton>
      </Group>
      <Popover
        className={cn(
          'rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none',
          'data-[entering]:animate-in data-[entering]:fade-in-0 data-[entering]:zoom-in-95',
          'data-[exiting]:animate-out data-[exiting]:fade-out-0 data-[exiting]:zoom-out-95',
          inModal ? 'z-[100] [pointer-events:auto!important]' : 'z-50'
        )}
        shouldCloseOnInteractOutside={(element) => {
          // No cerrar si se hace clic dentro del DatePicker
          const target = element as HTMLElement;

          // Check if the element is part of the DatePicker component
          if (target.closest('[data-slot="date-picker"]')) {
            return false;
          }

          // Check if the element is part of the popover/calendar
          if (target.closest('[role="dialog"]') || target.closest('[role="application"]')) {
            return false;
          }

          // Check if the element has focus-related attributes
          if (target.hasAttribute('data-focused') || target.getAttribute('role') === 'spinbutton') {
            return false;
          }

          // Allow closing otherwise
          return true;
        }}
      >
        <Dialog className="outline-none">
          <Calendar className="w-full">
            <header className="flex items-center justify-between pb-4">
              <AriaButton
                slot="previous"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4" />
              </AriaButton>
              <Heading className="text-sm font-medium" />
              <AriaButton
                slot="next"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
              >
                <ChevronRight className="h-4 w-4" />
              </AriaButton>
            </header>
            <CalendarGrid className="border-collapse space-y-1">
              {(date) => (
                <CalendarCell
                  date={date}
                  className={cn(
                    'relative h-9 w-9 text-center text-sm p-0 font-normal outline-none cursor-pointer',
                    'focus:z-10 focus:ring-2 focus:ring-ring focus:ring-offset-2',
                    'rounded-md',
                    // Today
                    'data-[today]:bg-accent data-[today]:text-accent-foreground',
                    // Selected
                    'data-[selected]:bg-primary data-[selected]:text-primary-foreground data-[selected]:hover:bg-primary data-[selected]:hover:text-primary-foreground data-[selected]:focus:bg-primary data-[selected]:focus:text-primary-foreground',
                    // Disabled
                    'data-[disabled]:text-muted-foreground data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed',
                    // Outside month
                    'data-[outside-month]:text-muted-foreground data-[outside-month]:opacity-50',
                    // Hover
                    'hover:bg-accent hover:text-accent-foreground',
                    // Unavailable
                    'data-[unavailable]:text-destructive data-[unavailable]:line-through data-[unavailable]:cursor-not-allowed'
                  )}
                />
              )}
            </CalendarGrid>
          </Calendar>
        </Dialog>
      </Popover>
    </AriaDatePicker>
  );
}

// Helper function to convert Date to CalendarDate
export function dateToCalendarDate(date: Date | string | null | undefined): CalendarDate | null {
  if (!date) return null;

  try {
    // If it's a string, parse it as a local date
    if (typeof date === 'string') {
      // Handle ISO date strings (YYYY-MM-DD) as local dates
      const isoMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) {
        const [, year, month, day] = isoMatch;
        return new CalendarDate(
          parseInt(year, 10),
          parseInt(month, 10),
          parseInt(day, 10)
        );
      }

      // For other string formats, try parsing
      const parsed = new Date(date);
      if (isNaN(parsed.getTime())) return null;
      date = parsed;
    }

    if (!(date instanceof Date)) return null;
    if (isNaN(date.getTime())) return null;

    // Use local timezone methods to match calendarDateToDate behavior
    // This ensures consistent round-trip conversion: Date -> CalendarDate -> Date
    return new CalendarDate(
      date.getFullYear(),
      date.getMonth() + 1,
      date.getDate()
    );
  } catch {
    return null;
  }
}

// Helper function to convert CalendarDate to Date
export function calendarDateToDate(calendarDate: DateValue | null | undefined): Date | null {
  if (!calendarDate) return null;

  try {
    // Create date in local timezone at midnight
    // This ensures that CalendarDate(2025, 10, 14) becomes October 14, 2025 in the user's timezone
    return new Date(
      calendarDate.year,
      calendarDate.month - 1,
      calendarDate.day,
      0, 0, 0, 0 // midnight local time
    );
  } catch {
    return null;
  }
}

export default DatePicker;
